/**
 * Click-to-Call button — direct-first VOIP orchestration.
 *
 * One button, one click. The DEFAULT is the proven browser-WebRTC path:
 * the customer rings directly, sees the CleanBid business caller-ID, and
 * there is NO personal-phone-first bridge.
 *
 *   supported browser (+ not opted out) → WebRTC (direct)
 *   else / on failure                   → phone bridge (fallback)
 *
 * The phone bridge (Telnyx rings the operator's verified phone first, then
 * dials the customer and bridges the legs) is used ONLY when this browser
 * can't do WebRTC, the WebRTC attempt fails (mic denied / registration /
 * timeout), or the operator explicitly opted into it (My Account →
 * Advanced). Fallback is always clearly labelled — PSTN is never presented
 * as a direct call. In every path the customer sees the CleanBid caller-ID.
 *
 * Phone-number handling
 * ---------------------
 * `normalizePhone` runs once at the prop boundary so every downstream
 * dial path receives a valid E.164.
 */
import React, { useState, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import VerifyPhoneDialog from './VerifyPhoneDialog';
import { getWebRTC } from '../../lib/webrtcClient';
import useDialPreference from '../../hooks/useDialPreference';
import { normalizePhone } from '../../utils/normalizePhone';

const API = process.env.REACT_APP_BACKEND_URL || '';

const fetchToken = async () => getAuth().currentUser?.getIdToken();

// True when this browser can actually place a WebRTC audio call — a
// secure context with getUserMedia. Works on BOTH desktop and mobile
// browsers (iOS Safari / Android Chrome do WebRTC audio). We deliberately
// do NOT gate on a desktop pointer or on the SDK being pre-registered —
// the proven flow registers on demand inside dialWebRTC. When this returns
// false (or the attempt fails), we transparently fall back to the phone
// bridge, always clearly labelled.
const _webrtcSupportable = () => {
  try {
    if (typeof window === 'undefined') return false;
    if (window.isSecureContext === false) return false;
    if (!navigator?.mediaDevices?.getUserMedia) return false;
    return true;
  } catch (_) { return false; }
};

const CallButton = ({
  phone: rawPhone,
  conversationId,
  children,
  className = '',
  title = 'Call customer',
  disabled = false,
  surface,
  'data-testid': testId,
  onAfterDial,
}) => {
  const phone = normalizePhone(rawPhone);
  const phoneInvalid = Boolean(rawPhone) && !phone;

  const [busy, setBusy] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [webrtcBusy, setWebrtcBusy] = useState(false);

  // Opt-out only: operators can force the phone bridge via My Account →
  // Advanced. Default is direct browser calling.
  const { preferBrowser, hasChosen, ready: prefReady } = useDialPreference();

  // ── Phone bridge: Telnyx rings the operator's verified phone first,
  //    then dials the customer with the KRO caller-ID. ─────────────────
  const performInitiate = useCallback(async () => {
    if (!phone) {
      toast.error('No phone number on this contact');
      return;
    }
    setBusy(true);
    try {
      const token = await fetchToken();
      const res = await fetch(`${API}/api/calls/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          customerPhone: phone,
          conversationId: conversationId || null,
        }),
      });
      if (res.status === 409) { toast.error('Another call is already in progress'); return; }
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || 'Could not start the call');
        return;
      }
      toast.success('Calling your phone…', { description: 'Pick up — we\'ll connect you to the customer.' });
      onAfterDial?.();
    } catch (err) {
      toast.error('Could not reach the server');
    } finally {
      setBusy(false);
    }
  }, [phone, conversationId, onAfterDial]);

  const dialBridge = useCallback(async () => {
    if (busy || disabled) return;
    setBusy(true);
    try {
      const token = await fetchToken();
      const res = await fetch(`${API}/api/calls/verified-number`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { toast.error('Could not check verified number'); return; }
      const j = await res.json();
      if (!j.verifiedNumber) { setVerifyOpen(true); return; }
      setBusy(false);
      await performInitiate();
    } catch (err) {
      toast.error('Could not reach the server');
    } finally {
      setBusy(false);
    }
  }, [busy, disabled, performInitiate]);

  // ── WebRTC (PRIMARY): ring the customer directly from this tab. ──────
  //    register() + placeCall() is the exact proven browser-WebRTC path.
  //    On ANY failure (unsupported / mic denied / registration or initiate
  //    failure / timeout) we transparently fall back to the phone bridge —
  //    clearly labelled so PSTN is never presented as a direct call.
  const dialWebRTC = useCallback(async () => {
    if (!phone) { toast.error('No phone number on this contact'); return; }
    setWebrtcBusy(true);

    const fallbackToBridge = async (reason) => {
      console.warn('[CallButton] WebRTC unavailable → phone bridge:', reason);
      toast.message('Couldn’t start a direct call — ringing your phone instead', {
        description: 'Pick up and we’ll connect you to the customer.',
      });
      setWebrtcBusy(false);
      await dialBridge();
    };

    try {
      const wrtc = getWebRTC();
      toast.message('Starting a direct call…', { description: phone });

      // Race register+initiate against a timeout so a stuck SDK (bad network,
      // browser WebRTC quirk) falls back to the bridge instead of hanging.
      const attempt = (async () => {
        if (wrtc.getState().status !== 'registered') await wrtc.register();
        await wrtc.placeCall({
          destination: phone,
          conversationId: conversationId || null,
          surface: surface || 'inbox-webrtc',
        });
      })();
      let timer;
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('webrtc-timeout')), 15000);
      });
      try {
        await Promise.race([attempt, timeout]);
      } finally {
        clearTimeout(timer);
      }

      toast.success('Calling the customer directly…', { description: phone });
      onAfterDial?.();
    } catch (err) {
      // Telnyx surfaced a hard rejection detail → still fall back, but log it.
      await fallbackToBridge(err?.telnyx?.detail || err?.message || 'error');
    } finally {
      setWebrtcBusy(false);
    }
  }, [phone, conversationId, surface, onAfterDial, dialBridge]);

  // Route the call. PRIMARY = direct browser WebRTC whenever this browser
  // supports it; the phone bridge is used only when WebRTC is unsupported
  // or the operator has explicitly opted into it.
  const routeCall = useCallback((wantsBrowser) => {
    if (wantsBrowser && _webrtcSupportable()) {
      dialWebRTC();
    } else {
      dialBridge();
    }
  }, [dialBridge, dialWebRTC]);

  // ── Routing rule: WebRTC-first ──────────────────────────────────────
  const dial = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (disabled) return;
    if (phoneInvalid) {
      toast.error('Invalid phone number', {
        description: `Could not parse "${rawPhone}" into a dialable format. Please update the contact.`,
      });
      return;
    }
    if (!phone) {
      toast.error('No phone number on this contact');
      return;
    }

    // Direct browser calling is the default. The ONLY thing that forces the
    // phone bridge is an explicit operator opt-out (My Account → Advanced),
    // or a browser that can't do WebRTC (handled inside routeCall).
    const explicitlyChosePhone = Boolean(prefReady && hasChosen && !preferBrowser);
    routeCall(!explicitlyChosePhone);
  }, [
    disabled, phone, phoneInvalid, rawPhone,
    prefReady, hasChosen, preferBrowser, routeCall,
  ]);

  return (
    <>
      <button
        type="button"
        title={title}
        onClick={dial}
        disabled={disabled || busy || webrtcBusy}
        className={className}
        data-testid={testId || 'click-to-call-btn'}
      >
        {(busy || webrtcBusy) ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
      </button>

      <VerifyPhoneDialog
        open={verifyOpen}
        onOpenChange={setVerifyOpen}
        onVerified={() => { setVerifyOpen(false); performInitiate(); }}
      />
    </>
  );
};

export default CallButton;

